import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ThreeEvent, useFrame } from '@react-three/fiber';
import { Html, Float } from '@react-three/drei';
import {
  Mesh,
  Group,
  CanvasTexture,
  Color,
  DoubleSide,
  BackSide,
  SRGBColorSpace,
  Vector3,
  Object3D,
} from 'three';
import * as THREE from 'three';
import { Project } from '../types/Project';
import { sunCoreRef } from '../scene/sceneRefs';
import OrbitRing from './OrbitRing';
import Satellite from './Satellite';

// Cache generated textures per project/color to avoid regenerating on re-mounts
const textureCache = new Map<string, {
  baseTexture?: CanvasTexture;
  detailTexture?: CanvasTexture;
  detailMaskTexture?: CanvasTexture;
  detailGlowTexture?: CanvasTexture;
}>();

// Create small neutral textures to keep material map slots stable (avoid shader recompiles)
function createSolidTexture(r: number, g: number, b: number, a = 255) {
  if (typeof document === 'undefined') return undefined as unknown as CanvasTexture;
  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = 1;
  const ctx = canvas.getContext('2d');
  if (!ctx) return undefined as unknown as CanvasTexture;
  const imgData = ctx.createImageData(1, 1);
  imgData.data[0] = r; imgData.data[1] = g; imgData.data[2] = b; imgData.data[3] = a;
  ctx.putImageData(imgData, 0, 0);
  const tex = new CanvasTexture(canvas);
  tex.colorSpace = SRGBColorSpace;
  tex.anisotropy = 1;
  tex.needsUpdate = true;
  return tex;
}

interface PlanetProps {
  project: Project;
  isFocused: boolean;
  onFocus: (project: Project, position: Vector3, scale: number) => void;
  onRequestDetails: (project: Project) => void;
  onFocusMove?: (project: Project, position: Vector3, scale: number) => void;
  initialAngle?: number;
  activeDetailProjectId?: string | null;
  globalReveal?: number; // 0..1 fade-in multiplier for appearance
  paused?: boolean; // pause all rotations & orbits
  speedMultiplier?: number; // global multiplier from user slider (0..2 typically)
  activeCategory?: string | null;
}

const Planet: React.FC<PlanetProps> = ({ project, isFocused, onFocus, onRequestDetails, onFocusMove, initialAngle, activeDetailProjectId, globalReveal = 1, paused = false, speedMultiplier = 1, activeCategory = null }) => {
  const orbitRef = useRef<Group>(null);
  const planetRef = useRef<Mesh>(null);
  const atmosphereRef = useRef<Mesh>(null);
  const cloudsRef = useRef<Mesh>(null);
  const detailRingRef = useRef<Group>(null);
  const worldPosition = useMemo(() => new Vector3(), []);

  // Filtering Logic
  const isVisible = useMemo(() => {
    if (!activeCategory) return true;
    return project.categories?.includes(activeCategory as any);
  }, [activeCategory, project.categories]);

  const [visualScale, setVisualScale] = useState(1);
  const [visualOpacity, setVisualOpacity] = useState(1);

  useFrame((state, delta) => {
    // Smooth transition for filtering
    const targetScale = isVisible ? 1 : 0.001;
    const targetOpacity = isVisible ? 1 : 0;

    // Lerp values
    const lerpSpeed = 4;
    setVisualScale(prev => THREE.MathUtils.lerp(prev, targetScale, delta * lerpSpeed));
    setVisualOpacity(prev => THREE.MathUtils.lerp(prev, targetOpacity, delta * lerpSpeed));
  });

  const generateTextures = (size: number) => {
    // existing generation body will be here
  };

  // Generate low-res textures once, with caching across mounts
  const { baseTexture, detailTexture, detailMaskTexture, detailGlowTexture } = useMemo(() => {
    if (typeof document === 'undefined') {
      return {
        baseTexture: undefined,
        detailTexture: undefined,
        detailMaskTexture: undefined,
        detailGlowTexture: undefined,
      };
    }

    const cacheKey = `${project.id}:${project.color}`;
    // console.log('Creating texture for project:', project.title, 'with color:', project.color, 'cacheKey:', cacheKey);

    const cached = textureCache.get(cacheKey);
    if (cached) {
      // console.log('Using cached texture for:', project.title);
      return cached;
    }

    const size = 512;
    const baseCanvas = document.createElement('canvas');
    baseCanvas.width = size;
    baseCanvas.height = size;
    const baseContext = baseCanvas.getContext('2d');
    if (!baseContext) {
      return {
        baseTexture: undefined,
        detailTexture: undefined,
        detailMaskTexture: undefined,
        detailGlowTexture: undefined,
      };
    }

    const base = new Color(project.color);
    const darker = base.clone().offsetHSL(0, 0, -0.18);
    const lighter = base.clone().offsetHSL(0, -0.05, 0.16);
    const accent = base.clone().offsetHSL(0.02, 0.12, 0.06);

    baseContext.fillStyle = base.getStyle();
    baseContext.fillRect(0, 0, size, size);

    const baseBands = 6;
    for (let i = 0; i < baseBands; i++) {
      const thickness = size / (baseBands * 2) + Math.random() * (size / 30);
      const y = (i / baseBands) * size + thickness * 0.5 + Math.random() * (size / 12);
      const gradient = baseContext.createLinearGradient(0, y, size, y + thickness);
      gradient.addColorStop(0, darker.getStyle());
      gradient.addColorStop(0.35, base.clone().offsetHSL(0, -0.08, -0.03).getStyle());
      gradient.addColorStop(0.7, accent.getStyle());
      gradient.addColorStop(1, lighter.getStyle());
      baseContext.globalAlpha = 0.55 - i * 0.05;
      baseContext.fillStyle = gradient;
      baseContext.fillRect(0, y, size, thickness);
    }

    baseContext.globalAlpha = 0.28;
    for (let i = 0; i < size * 0.15; i++) {
      const radius = Math.random() * 6 + 3;
      const x = Math.random() * size;
      const y = Math.random() * size;
      const radial = baseContext.createRadialGradient(x, y, 0, x, y, radius);
      radial.addColorStop(0, lighter.getStyle());
      radial.addColorStop(1, 'rgba(255, 255, 255, 0)');
      baseContext.fillStyle = radial;
      baseContext.fillRect(x - radius, y - radius, radius * 2, radius * 2);
    }

    const detailCanvas = document.createElement('canvas');
    detailCanvas.width = size;
    detailCanvas.height = size;
    const detailContext = detailCanvas.getContext('2d');
    if (!detailContext) {
      const fallbackTexture = new CanvasTexture(baseCanvas);
      fallbackTexture.colorSpace = SRGBColorSpace;
      fallbackTexture.anisotropy = 4;
      fallbackTexture.needsUpdate = true;
      return {
        baseTexture: fallbackTexture,
        detailTexture: fallbackTexture,
        detailMaskTexture: fallbackTexture,
        detailGlowTexture: fallbackTexture,
      };
    }

    detailContext.drawImage(baseCanvas, 0, 0);

    const highContrast = base.clone().offsetHSL(-0.08, 0.2, 0.12);
    const glow = base.clone().offsetHSL(0.1, 0.25, 0.35);

    detailContext.globalAlpha = 0.4;
    for (let i = 0; i < 10; i++) {
      const bandHeight = size / (12 + i * 0.8);
      const y = (i / 10) * size + Math.random() * (size / 20);
      const gradient = detailContext.createLinearGradient(0, y, size, y + bandHeight);
      gradient.addColorStop(0, highContrast.clone().offsetHSL(0, 0, -0.1).getStyle());
      gradient.addColorStop(0.5, glow.getStyle());
      gradient.addColorStop(1, lighter.clone().offsetHSL(0.05, -0.1, 0.08).getStyle());
      detailContext.fillStyle = gradient;
      detailContext.fillRect(0, y, size, bandHeight);
    }

    detailContext.globalAlpha = 0.32;
    for (let i = 0; i < size * 0.35; i++) {
      const radius = Math.random() * 3 + 1;
      const x = Math.random() * size;
      const y = Math.random() * size;
      const radial = detailContext.createRadialGradient(x, y, 0, x, y, radius);
      radial.addColorStop(0, glow.getStyle());
      radial.addColorStop(0.5, lighter.getStyle());
      radial.addColorStop(1, 'rgba(255, 255, 255, 0)');
      detailContext.fillStyle = radial;
      detailContext.fillRect(x - radius, y - radius, radius * 2, radius * 2);
    }

    detailContext.globalAlpha = 0.25;
    detailContext.lineWidth = 2;
    for (let i = 0; i < 24; i++) {
      const startX = Math.random() * size;
      const startY = Math.random() * size;
      const endX = startX + (Math.random() - 0.5) * size * 0.65;
      const endY = startY + (Math.random() - 0.5) * size * 0.18;
      const gradient = detailContext.createLinearGradient(startX, startY, endX, endY);
      gradient.addColorStop(0, 'rgba(255, 255, 255, 0)');
      gradient.addColorStop(0.35, glow.clone().offsetHSL(0.08, 0.05, 0.12).getStyle());
      gradient.addColorStop(0.65, lighter.clone().offsetHSL(0.04, -0.08, 0.18).getStyle());
      gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
      detailContext.strokeStyle = gradient;
      detailContext.beginPath();
      detailContext.moveTo(startX, startY);
      detailContext.bezierCurveTo(
        (startX + endX) / 2 + Math.random() * size * 0.12,
        startY + Math.random() * size * 0.22,
        (startX + endX) / 2 - Math.random() * size * 0.12,
        endY - Math.random() * size * 0.24,
        endX,
        endY
      );
      detailContext.stroke();
    }

    const glowCanvas = document.createElement('canvas');
    glowCanvas.width = size;
    glowCanvas.height = size;
    const glowContext = glowCanvas.getContext('2d');
    if (!glowContext) {
      const fallbackTexture = new CanvasTexture(detailCanvas);
      fallbackTexture.colorSpace = SRGBColorSpace;
      fallbackTexture.anisotropy = 4;
      fallbackTexture.needsUpdate = true;
      return {
        baseTexture: fallbackTexture,
        detailTexture: fallbackTexture,
        detailMaskTexture: fallbackTexture,
        detailGlowTexture: fallbackTexture,
      };
    }

    glowContext.clearRect(0, 0, size, size);
    glowContext.globalCompositeOperation = 'lighter';
    glowContext.globalAlpha = 0.66;

    for (let i = 0; i < 42; i++) {
      const startX = Math.random() * size;
      const startY = Math.random() * size;
      const controlOffset = size * 0.08 + Math.random() * size * 0.12;
      const endX = startX + (Math.random() - 0.5) * size * 0.55;
      const endY = startY + (Math.random() - 0.5) * size * 0.25;
      const gradient = glowContext.createLinearGradient(startX, startY, endX, endY);
      gradient.addColorStop(0, 'rgba(255, 255, 255, 0)');
      gradient.addColorStop(0.4, glow.clone().offsetHSL(0.02, 0.25, 0.25).getStyle());
      gradient.addColorStop(0.6, lighter.clone().offsetHSL(0.04, -0.08, 0.22).getStyle());
      gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
      glowContext.strokeStyle = gradient;
      glowContext.lineWidth = size * 0.005;
      glowContext.beginPath();
      glowContext.moveTo(startX, startY);
      glowContext.bezierCurveTo(
        startX + (Math.random() > 0.5 ? controlOffset : -controlOffset),
        startY + Math.random() * controlOffset,
        endX - (Math.random() > 0.5 ? controlOffset : -controlOffset),
        endY - Math.random() * controlOffset,
        endX,
        endY
      );
      glowContext.stroke();
    }

    glowContext.globalAlpha = 0.55;
    for (let i = 0; i < 18; i++) {
      const cx = Math.random() * size;
      const cy = Math.random() * size;
      const rx = size * (0.05 + Math.random() * 0.12);
      const ry = rx * (0.6 + Math.random() * 0.6);
      const rotation = Math.random() * Math.PI;
      glowContext.save();
      glowContext.translate(cx, cy);
      glowContext.rotate(rotation);
      const gradient = glowContext.createRadialGradient(0, 0, 0, 0, 0, rx);
      gradient.addColorStop(0, lighter.clone().offsetHSL(0.12, -0.2, 0.35).getStyle());
      gradient.addColorStop(0.6, glow.clone().offsetHSL(-0.05, 0.35, 0.2).getStyle());
      gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
      glowContext.fillStyle = gradient;
      glowContext.beginPath();
      glowContext.ellipse(0, 0, rx, ry, 0, 0, Math.PI * 2);
      glowContext.fill();
      glowContext.restore();
    }

    glowContext.globalAlpha = 0.35;
    glowContext.filter = 'blur(2px)';
    glowContext.drawImage(glowCanvas, -1, -1, size + 2, size + 2);
    glowContext.filter = 'none';
    glowContext.globalAlpha = 1;

    // --- Emissive logo/text stamp on glow canvas (pre-texture creation) ---
    // Fallback monogram based on primary technology or project title initial
    const primaryTech = (project.technologies && project.technologies[0]) ? project.technologies[0] : '';
    const monogram = (primaryTech || project.title || 'P').trim().charAt(0).toUpperCase();
    // Position near equator, slightly offset to avoid seam
    const uvX = 0.68; // 0..1
    const uvY = 0.44; // 0..1
    const px = uvX * size;
    const py = uvY * size;
    const logoSize = size * 0.16; // diameter/height for text or image

    // Draw soft circle glow underlay for better visibility
    const underGlow = glowContext.createRadialGradient(px, py, 0, px, py, logoSize * 0.8);
    underGlow.addColorStop(0, 'rgba(255,255,255,0.5)');
    underGlow.addColorStop(1, 'rgba(255,255,255,0)');
    glowContext.globalAlpha = 0.4;
    glowContext.fillStyle = underGlow;
    glowContext.beginPath();
    glowContext.arc(px, py, logoSize * 0.9, 0, Math.PI * 2);
    glowContext.fill();
    glowContext.globalAlpha = 1;

    // Fallback: crisp monogram text on glow
    glowContext.save();
    glowContext.font = `${Math.floor(logoSize)}px sans-serif`;
    glowContext.textAlign = 'center';
    glowContext.textBaseline = 'middle';
    glowContext.fillStyle = '#ffffff';
    glowContext.shadowColor = 'rgba(255,255,255,0.8)';
    glowContext.shadowBlur = logoSize * 0.35;
    glowContext.fillText(monogram, px, py);
    glowContext.restore();

    detailContext.globalAlpha = 0.24;
    detailContext.globalCompositeOperation = 'overlay';
    detailContext.drawImage(glowCanvas, 0, 0);
    detailContext.globalCompositeOperation = 'source-over';
    detailContext.globalAlpha = 1;

    const maskCanvas = document.createElement('canvas');
    maskCanvas.width = size;
    maskCanvas.height = size;
    const maskContext = maskCanvas.getContext('2d');
    if (!maskContext) {
      const fallbackTexture = new CanvasTexture(detailCanvas);
      fallbackTexture.colorSpace = SRGBColorSpace;
      fallbackTexture.anisotropy = 4;
      fallbackTexture.needsUpdate = true;
      return {
        baseTexture: fallbackTexture,
        detailTexture: fallbackTexture,
        detailMaskTexture: fallbackTexture,
        detailGlowTexture: fallbackTexture,
      };
    }

    const maskImage = maskContext.createImageData(size, size);
    const maskData = maskImage.data;
    const hslHolder = { h: 0, s: 0, l: 0 };
    base.getHSL(hslHolder);
    const harmonicSeed = hslHolder.h * Math.PI * 2;
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const nx = x / size;
        const ny = y / size;
        const wave = Math.sin(nx * 24 + ny * 32 + harmonicSeed) * 0.12;
        const ridge = Math.sin((nx + ny) * Math.PI * 8 + Math.sin(nx * 6)) * 0.08;
        const turbulence = Math.sin(nx * 18 + Math.cos(ny * 28)) * 0.05;
        const micro = Math.sin((nx - ny) * Math.PI * 20) * 0.04;
        const value = 0.55 + wave + ridge + turbulence + micro;
        const clamped = Math.max(0.18, Math.min(0.9, value));
        const shade = Math.floor(clamped * 255);
        const index = (y * size + x) * 4;
        maskData[index] = shade;
        maskData[index + 1] = shade;
        maskData[index + 2] = shade;
        maskData[index + 3] = 255;
      }
    }
    maskContext.putImageData(maskImage, 0, 0);

    maskContext.globalAlpha = 0.4;
    for (let i = 0; i < size * 0.45; i++) {
      const radius = Math.random() * 3 + 0.8;
      const x = Math.random() * size;
      const y = Math.random() * size;
      const shade = Math.random() > 0.5 ? 220 : 80;
      maskContext.fillStyle = `rgba(${shade}, ${shade}, ${shade}, 0.5)`;
      maskContext.beginPath();
      maskContext.arc(x, y, radius, 0, Math.PI * 2);
      maskContext.fill();
    }

    // Stamp a subtle bump mask for the logo area
    maskContext.globalAlpha = 0.9;
    maskContext.fillStyle = 'rgba(245,245,245,0.95)';
    maskContext.font = `${Math.floor(logoSize)}px sans-serif`;
    maskContext.textAlign = 'center';
    maskContext.textBaseline = 'middle';
    maskContext.fillText(monogram, px, py);

    const baseTexture = new CanvasTexture(baseCanvas);
    baseTexture.colorSpace = SRGBColorSpace;
    baseTexture.anisotropy = 4;
    baseTexture.needsUpdate = true;

    const detailTexture = new CanvasTexture(detailCanvas);
    detailTexture.colorSpace = SRGBColorSpace;
    detailTexture.anisotropy = 4;
    detailTexture.needsUpdate = true;

    const detailMaskTexture = new CanvasTexture(maskCanvas);
    detailMaskTexture.colorSpace = SRGBColorSpace;
    detailMaskTexture.anisotropy = 4;
    detailMaskTexture.needsUpdate = true;

    const detailGlowTexture = new CanvasTexture(glowCanvas);
    detailGlowTexture.colorSpace = SRGBColorSpace;
    detailGlowTexture.anisotropy = 4;
    detailGlowTexture.needsUpdate = true;

    // Resolve logo URL for the surface stamp (always use technology logo for consistency)
    const primaryTechForTexture = (project.technologies && project.technologies[0]) ? project.technologies[0] : '';
    const normalizedForTexture = primaryTechForTexture.toLowerCase().replace(/[^a-z0-9+]/g, '');
    const logoMap: Record<string, string> = {
      react: 'https://cdn.simpleicons.org/react',
      nextjs: 'https://cdn.simpleicons.org/nextdotjs',
      vuejs: 'https://cdn.simpleicons.org/vuedotjs',
      vue: 'https://cdn.simpleicons.org/vuedotjs',
      svelte: 'https://cdn.simpleicons.org/svelte',
      typescript: 'https://cdn.simpleicons.org/typescript',
      javascript: 'https://cdn.simpleicons.org/javascript',
      nodejs: 'https://cdn.simpleicons.org/nodedotjs',
      node: 'https://cdn.simpleicons.org/nodedotjs',
      python: 'https://cdn.simpleicons.org/python',
      java: 'https://cdn.simpleicons.org/openjdk',
      spring: 'https://cdn.simpleicons.org/spring',
      go: 'https://cdn.simpleicons.org/go',
      golang: 'https://cdn.simpleicons.org/go',
      rust: 'https://cdn.simpleicons.org/rust',
      mongodb: 'https://cdn.simpleicons.org/mongodb',
      postgresql: 'https://cdn.simpleicons.org/postgresql',
      postgres: 'https://cdn.simpleicons.org/postgresql',
      stripe: 'https://cdn.simpleicons.org/stripe',
      socketio: 'https://cdn.simpleicons.org/socketdotio',
      express: 'https://cdn.simpleicons.org/express',
      tensorflow: 'https://cdn.simpleicons.org/tensorflow',
      fastapi: 'https://cdn.simpleicons.org/fastapi',
      prisma: 'https://cdn.simpleicons.org/prisma',
      threejs: 'https://cdn.simpleicons.org/threedotjs',
      aws: 'https://cdn.simpleicons.org/amazonaws',
      reactnative: 'https://cdn.simpleicons.org/react',
      firebase: 'https://cdn.simpleicons.org/firebase',
      chartjs: 'https://cdn.simpleicons.org/chartdotjs',
    };

    // Use the mapped technology logo if available, otherwise fallback to project.logoUrl
    const textureLogoUrl = logoMap[normalizedForTexture] || project.logoUrl;

    if (textureLogoUrl) {
      try {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          const drawSize = logoSize * 1.2;
          const dx = px - drawSize / 2;
          const dy = py - drawSize / 2;

          // Draw stronger emissive image
          glowContext.save();
          glowContext.globalCompositeOperation = 'lighter';
          glowContext.globalAlpha = 0.9;
          glowContext.drawImage(img, dx, dy, drawSize, drawSize);
          glowContext.restore();
          detailGlowTexture.needsUpdate = true;

          // Draw into mask for slight bump/roughness change
          const grayOverlay = document.createElement('canvas');
          grayOverlay.width = img.width;
          grayOverlay.height = img.height;
          const gctx = grayOverlay.getContext('2d');
          if (gctx) {
            gctx.drawImage(img, 0, 0);
            const imgData = gctx.getImageData(0, 0, img.width, img.height);
            const data = imgData.data;
            for (let i = 0; i < data.length; i += 4) {
              const r = data[i], g = data[i + 1], b = data[i + 2];
              const l = Math.min(255, (r * 0.299 + g * 0.587 + b * 0.114) * 1.15);
              data[i] = data[i + 1] = data[i + 2] = l;
            }
            gctx.putImageData(imgData, 0, 0);
            maskContext.globalAlpha = 0.95;
            maskContext.drawImage(grayOverlay, dx, dy, drawSize, drawSize);
            detailMaskTexture.needsUpdate = true;
          }
        };
        img.src = textureLogoUrl;
      } catch (e) {
        // swallow logo errors to keep runtime stable
      }
    }

    const generated = { baseTexture, detailTexture, detailMaskTexture, detailGlowTexture };
    // console.log('Generated NEW texture for project:', project.title);
    textureCache.set(cacheKey, generated);
    return generated;
  }, [project.id, project.color]);

  const hasRing = project.orbitRadius > 28 && project.id !== '1';
  // Slightly larger planets globally
  const planetScale = project.size * 1.12;
  const detailActive = activeDetailProjectId === project.id;
  const detailScaleMultiplier = detailActive ? 1.65 : 1;
  const planetDisplayScale = planetScale * detailScaleMultiplier;
  const ringInner = planetDisplayScale * 1.35;
  const ringOuter = planetDisplayScale * 1.9;
  const ringColor = useMemo(() => new Color(project.color).offsetHSL(-0.02, -0.1, 0.25), [project.color]);
  const focalScale = planetDisplayScale;
  const cloudScale = planetDisplayScale * 1.04;
  const atmosphereScale = planetDisplayScale * 1.16;
  const truncatedDescription = useMemo(() => {
    const text = project.description ?? '';
    return text.length > 250 ? `${text.slice(0, 250)}...` : text;
  }, [project.description]);
  const technologiesSummary = useMemo(() => project.technologies.slice(0, 4).join(' | '), [project.technologies]);
  // Further reduced orbital revolution speed (was orbitSpeed * 0.35)
  const baseOrbitSpeed = project.orbitSpeed * 0.15;
  const focusedOrbitSpeed = baseOrbitSpeed * (detailActive ? 0.1 : 0.25);
  const showOverlay = isFocused && !detailActive;
  const isAnyDetailMode = Boolean(activeDetailProjectId);
  const showLogoChip = !isAnyDetailMode && !isFocused && !detailActive; // hide all chips in detail mode

  // Resolve a logo URL from project.logoUrl or primary technology name
  const primaryTech = (project.technologies && project.technologies[0]) ? project.technologies[0] : '';
  const normalized = primaryTech.toLowerCase().replace(/[^a-z0-9+]/g, '');
  const mappedLogo = useMemo(() => {
    const map: Record<string, string> = {
      react: 'https://cdn.simpleicons.org/react',
      nextjs: 'https://cdn.simpleicons.org/nextdotjs',
      vuejs: 'https://cdn.simpleicons.org/vuedotjs',
      vue: 'https://cdn.simpleicons.org/vuedotjs',
      svelte: 'https://cdn.simpleicons.org/svelte',
      typescript: 'https://cdn.simpleicons.org/typescript',
      javascript: 'https://cdn.simpleicons.org/javascript',
      nodejs: 'https://cdn.simpleicons.org/nodedotjs',
      node: 'https://cdn.simpleicons.org/nodedotjs',
      python: 'https://cdn.simpleicons.org/python',
      java: 'https://cdn.simpleicons.org/openjdk',
      spring: 'https://cdn.simpleicons.org/spring',
      go: 'https://cdn.simpleicons.org/go',
      golang: 'https://cdn.simpleicons.org/go',
      rust: 'https://cdn.simpleicons.org/rust',
      mongodb: 'https://cdn.simpleicons.org/mongodb',
      postgresql: 'https://cdn.simpleicons.org/postgresql',
      postgres: 'https://cdn.simpleicons.org/postgresql',
      stripe: 'https://cdn.simpleicons.org/stripe',
      socketio: 'https://cdn.simpleicons.org/socketdotio',
      express: 'https://cdn.simpleicons.org/express',
      tensorflow: 'https://cdn.simpleicons.org/tensorflow',
      fastapi: 'https://cdn.simpleicons.org/fastapi',
      prisma: 'https://cdn.simpleicons.org/prisma',
      threejs: 'https://cdn.simpleicons.org/threedotjs',
      aws: 'https://cdn.simpleicons.org/amazonaws',
      reactnative: 'https://cdn.simpleicons.org/react',
      firebase: 'https://cdn.simpleicons.org/firebase',
      chartjs: 'https://cdn.simpleicons.org/chartdotjs',
      c: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/csharp/csharp-original.svg',
      csharp: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/csharp/csharp-original.svg',
    };
    if (map[normalized]) return map[normalized];
    if (project.logoUrl) return project.logoUrl;
    return '';
  }, [project.logoUrl, normalized]);
  const resolvedLogo = mappedLogo || '/logos/placeholder.svg';
  const [logoFailed, setLogoFailed] = useState(false);
  const detailSatellites = useMemo(() => {
    const items = project.technologies.slice(0, Math.min(project.technologies.length, 6));
    if (items.length === 0) {
      return [] as Array<{ label: string; angle: number; radius: number; height: number; speed: number }>;
    }
    return items.map((label, index) => {
      const angle = (index / items.length) * Math.PI * 2;
      const radius = planetDisplayScale * (2.1 + 0.35 * index); // bring orbits closer in detail view
      const height = Math.sin(angle * 2) * planetDisplayScale * 0.26; // gentle vertical wave
      const speed = 0.0018 + index * 0.00035; // slight per-satellite speed offset
      return { label, angle, radius, height, speed };
    });
  }, [planetDisplayScale, project.technologies]);

  // Neutral textures to keep map bindings stable (avoid shader swaps)
  const neutralGray = useMemo(() => createSolidTexture(128, 128, 128, 255), []);
  const neutralBlack = useMemo(() => createSolidTexture(0, 0, 0, 255), []);
  // Temporaire : toujours utiliser baseTexture pour debug
  const surfaceTexture = baseTexture ?? neutralGray;
  console.log('Planet', project.title, 'using texture. BaseTexture exists:', !!baseTexture);
  const detailEnhancementMap = (isFocused || detailActive) ? (detailMaskTexture ?? neutralGray) : neutralGray; // always present
  const detailEmissiveMap = (isFocused || detailActive) ? (detailGlowTexture ?? neutralBlack) : neutralBlack; // always present

  const surfaceRoughness = detailActive ? 0.32 : isFocused ? 0.42 : 0.55;
  const surfaceMetalness = detailActive ? 0.38 : isFocused ? 0.24 : 0.16;
  const surfaceEmissive = detailActive ? 0.55 : isFocused ? 0.36 : 0.18;
  // Bump and displacement trimmed further; displacement only in detailActive
  const bumpScale = detailActive ? 0.22 : isFocused ? 0.10 : 0.04;
  const displacementScale = detailActive ? 0.065 : 0; // disabled outside detail for stability
  const displacementBias = displacementScale ? -displacementScale * 0.35 : 0;

  useEffect(() => {
    if (orbitRef.current && typeof initialAngle === 'number') {
      orbitRef.current.rotation.set(0, initialAngle, 0);
    }
  }, [initialAngle]);

  // No effect needed: Html occlude supports refs array; we pass the shared Sun core ref

  // Centralized rotation speed factors for easy future tuning
  const SELF_ROTATE_Y = 0.006; // was 0.02
  const SELF_ROTATE_X = 0.003; // was 0.01
  const CLOUD_ROTATE_Y = 0.0055; // was 0.018
  const ATMOSPHERE_ROTATE_Y = 0.002; // was 0.006
  const DETAIL_RING_ROTATE_Y = 0.002; // slower base rotation; satellites add their own offset
  const ORBIT_SPEED_SCALE_FOCUS = focusedOrbitSpeed; // unchanged logic, already reduced when focused
  const ORBIT_SPEED_SCALE_BASE = baseOrbitSpeed * 0.5; // additional reduction

  const [revealProgress, setRevealProgress] = useState(0);

  useFrame((state, delta) => {
    // Cinematic sequential reveal progress
    const targetReveal = detailActive ? 1 : 0;
    // Speed: 0.8 for a snappy but visible "unfolding" (~1.2s total)
    setRevealProgress(prev => THREE.MathUtils.lerp(prev, targetReveal, delta * 0.8));

    if (!paused) {
      if (orbitRef.current) {
        // Pause orbit if details are active so the camera can focus on a stationary target
        const orbitSpeed = detailActive ? 0 : (isFocused ? ORBIT_SPEED_SCALE_FOCUS : ORBIT_SPEED_SCALE_BASE);
        orbitRef.current.rotation.y += orbitSpeed * speedMultiplier;
      }

      if (planetRef.current) {
        planetRef.current.rotation.y += SELF_ROTATE_Y * speedMultiplier;
        planetRef.current.rotation.x += SELF_ROTATE_X * speedMultiplier;
      }

      if (cloudsRef.current) {
        cloudsRef.current.rotation.y += CLOUD_ROTATE_Y * speedMultiplier;
      }

      if (atmosphereRef.current) {
        atmosphereRef.current.rotation.y += ATMOSPHERE_ROTATE_Y * speedMultiplier;
      }
    }

    if (isFocused && planetRef.current) {
      planetRef.current.getWorldPosition(worldPosition);
      onFocusMove?.(project, worldPosition, planetDisplayScale);
    }

    if (detailActive && detailRingRef.current && !paused) {
      detailRingRef.current.rotation.y += DETAIL_RING_ROTATE_Y * speedMultiplier;
      // Per-satellite additional rotation for a more "satellite" feel
      const parent = detailRingRef.current;
      let idx = 0;
      parent.children.forEach((child) => {
        if (child && 'rotation' in child) {
          const spd = detailSatellites[idx]?.speed ?? 0.0;
          // @ts-ignore
          child.rotation.y += spd * speedMultiplier;
          idx++;
        }
      });
    }
  });

  const handlePlanetClick = (event: ThreeEvent<MouseEvent>) => {
    event.stopPropagation();
    // If we are already in detail mode for this project, do nothing (prevent zoom out/re-focus)
    if (detailActive) return;

    if (!planetRef.current) {
      return;
    }
    planetRef.current.getWorldPosition(worldPosition);
    // Defer focus state update to next animation frame to keep this frame smooth
    const pos = worldPosition.clone();
    if (typeof requestAnimationFrame !== 'undefined') {
      requestAnimationFrame(() => onFocus(project, pos, planetDisplayScale));
    } else {
      setTimeout(() => onFocus(project, pos, planetDisplayScale), 0);
    }
  };

  const handleDetailsClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    console.log('handleDetailsClick called for planet:', project.title);
    event.stopPropagation();
    onRequestDetails(project);
  };

  // Always keep planet in orbit, never teleport to center
  const planetPosition: [number, number, number] = [project.orbitRadius, 0, 0];

  const [hovered, setHovered] = useState(false);

  const handlePointerOver = (e: ThreeEvent<PointerEvent>) => {
    e.stopPropagation();
    setHovered(true);
    document.body.style.cursor = 'pointer';
  };

  const handlePointerOut = (e: ThreeEvent<PointerEvent>) => {
    setHovered(false);
    document.body.style.cursor = 'auto';
  };

  const handleHtmlClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!planetRef.current) return;
    planetRef.current.getWorldPosition(worldPosition);
    const pos = worldPosition.clone();
    if (typeof requestAnimationFrame !== 'undefined') {
      requestAnimationFrame(() => onFocus(project, pos, planetDisplayScale));
    } else {
      setTimeout(() => onFocus(project, pos, planetDisplayScale), 0);
    }
  };

  const handleHtmlHover = (e: React.PointerEvent) => {
    e.stopPropagation();
    setHovered(true);
    document.body.style.cursor = 'pointer';
  };

  const handleHtmlOut = (e: React.PointerEvent) => {
    e.stopPropagation();
    setHovered(false);
    document.body.style.cursor = 'auto';
  };

  return (
    <group ref={orbitRef} visible={visualOpacity > 0.01}>
      {!detailActive && <OrbitRing radius={project.orbitRadius} color="#888888" alpha={globalReveal * visualOpacity} />}

      <group position={planetPosition} scale={visualScale}>
        {/* Hover Outline */}
        {!detailActive && hovered && (
          <mesh scale={focalScale * 1.22} onClick={handlePlanetClick}>
            <sphereGeometry args={[1, 56, 56]} />
            <meshBasicMaterial
              color="white"
              side={BackSide}
              transparent
              opacity={0.6}
              depthWrite={false}
            />
          </mesh>
        )}

        {/* Overview language/framework logo above the planet (with small chip) */}
        {showLogoChip && globalReveal > 0.05 && (
          <Float speed={1.2} rotationIntensity={0.0} floatIntensity={0.2}>
            <Html
              position={[0, planetDisplayScale * 1.75, 0]}
              center
              occlude={sunCoreRef.current ? [sunCoreRef as unknown as React.RefObject<Object3D>] : false}
              style={{ pointerEvents: 'none' }}
            >
              <div
                onClick={handleHtmlClick}
                onPointerOver={handleHtmlHover}
                onPointerOut={handleHtmlOut}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: Math.max(28, planetDisplayScale * 24),
                  height: Math.max(28, planetDisplayScale * 24),
                  borderRadius: '999px',
                  background: 'rgba(15, 23, 42, 0.7)',
                  border: '1px solid rgba(148, 163, 184, 0.35)',
                  boxShadow: '0 10px 26px rgba(15, 23, 42, 0.5)',
                  backdropFilter: 'blur(6px)',
                  overflow: 'hidden',
                  opacity: Math.min(1, Math.max(0.001, globalReveal * visualOpacity)),
                  pointerEvents: visualOpacity > 0.1 ? 'auto' : 'none',
                  cursor: 'pointer',
                }}
              >
                {/* eslint-disable-next-line jsx-a11y/alt-text */}
                <img
                  src={logoFailed ? '/logos/placeholder.svg' : resolvedLogo}
                  style={{ width: '70%', height: '70%', objectFit: 'contain', filter: 'drop-shadow(0 0 6px rgba(255,255,255,0.35))' }}
                  onError={() => setLogoFailed(true)}
                />
              </div>
            </Html>
          </Float>
        )}
        {hasRing && (
          <mesh
            rotation={[Math.PI / 2.2, 0, 0.35]}
            scale={planetDisplayScale}
            renderOrder={detailActive ? -1 : 0}
          >
            <ringGeometry args={[ringInner, ringOuter, 144]} />
            <meshPhongMaterial
              color={ringColor}
              transparent
              opacity={(detailActive ? 0.38 : isFocused ? 0.42 : 0.28) * globalReveal}
              side={DoubleSide}
              depthWrite={false}
            />
          </mesh>
        )}
        <mesh
          ref={planetRef}
          onClick={handlePlanetClick}
          onPointerOver={handlePointerOver}
          onPointerOut={handlePointerOut}
          scale={focalScale}
        >
          {/* Keep segment counts constant to avoid geometry reallocations on focus toggle */}
          <sphereGeometry args={[1, 56, 56]} />
          <meshStandardMaterial
            color={new Color(project.color).offsetHSL(-0.03, 0.05, -0.1)}
            emissive={new Color(project.color).offsetHSL(0.04, 0.1, 0.15)}
            emissiveIntensity={surfaceEmissive}
            roughness={surfaceRoughness}
            metalness={surfaceMetalness}
            map={surfaceTexture}
            emissiveMap={detailEmissiveMap}
            roughnessMap={detailEnhancementMap}
            metalnessMap={detailEnhancementMap}
            bumpMap={detailEnhancementMap}
            bumpScale={bumpScale}
            displacementMap={detailEnhancementMap}
            displacementScale={displacementScale}
            displacementBias={displacementBias}
            transparent
            opacity={Math.min(1, Math.max(0.001, globalReveal))}
          />
        </mesh>

        {isFocused && (
          <mesh scale={focalScale * 1.015} renderOrder={1} onClick={handlePlanetClick}>
            <sphereGeometry args={[1, 80, 80]} />
            <meshBasicMaterial
              color={new Color(project.color).offsetHSL(0.12, -0.25, 0.5)}
              map={detailEmissiveMap ?? detailTexture ?? baseTexture}
              transparent
              opacity={0.35 * globalReveal}
              depthWrite={false}
              toneMapped={false}
            />
          </mesh>
        )}

        <mesh
          ref={cloudsRef}
          scale={cloudScale}
          onClick={handlePlanetClick}
        >
          <sphereGeometry args={[1.02, 40, 40]} />
          <meshStandardMaterial
            color={new Color(project.color).offsetHSL(0.02, -0.25, 0.35)}
            transparent
            opacity={(isFocused ? 0.45 : 0.35) * globalReveal}
            roughness={0.85}
            metalness={0.02}
            depthWrite={false}
          />
        </mesh>

        <mesh
          ref={atmosphereRef}
          scale={atmosphereScale}
          onClick={handlePlanetClick}
        >
          <sphereGeometry args={[1.08, 40, 40]} />
          <meshBasicMaterial
            color={new Color(project.color).offsetHSL(0.05, -0.3, 0.4)}
            transparent
            opacity={(isFocused ? 0.3 : 0.18) * globalReveal}
            depthWrite={false}
            toneMapped={false}
          />
        </mesh>

        {showOverlay && (
          <Html
            position={[0, planetDisplayScale * 0.75, 0]}
            center
            distanceFactor={18}
            zIndexRange={[2000, 1000]} // Ensure it's always above satellites
            style={{ pointerEvents: 'none' }}
          >
            <div
              className="planet-summary-card"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="planet-summary-eyebrow">
                Projet
              </div>
              <div className="planet-summary-title">{project.title}</div>
              <div className="planet-summary-description">{truncatedDescription}</div>
              {technologiesSummary && (
                <div className="planet-summary-tech">
                  {technologiesSummary}
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={handleDetailsClick}
                  className="planet-summary-button"
                >
                  Voir détails
                </button>
              </div>
            </div>
          </Html>
        )}

        {detailActive && (
          <group>
            {/* Satellite orbit paths - Sequential Reveal */}
            {detailSatellites.map((sat, i) => {
              // Calculate sequential progress for this specific orbit
              // Stagger: 0.1s per item, Duration: 0.5s per item
              const start = i * 0.12;
              const end = start + 0.45;
              const localProgress = THREE.MathUtils.clamp((revealProgress - start) / (end - start), 0, 1);
              const smoothProgress = THREE.MathUtils.smoothstep(localProgress, 0, 1);

              // Scale expansion effect (0.9 -> 1.0)
              const orbitScale = 0.92 + 0.08 * smoothProgress;

              return (
                <group key={`orbit-group-${sat.label}`} scale={orbitScale}>
                  <mesh rotation={[Math.PI / 2, 0, 0]} renderOrder={-3}>
                    <ringGeometry args={[sat.radius - planetDisplayScale * 0.035, sat.radius + planetDisplayScale * 0.035, 96]} />
                    <meshBasicMaterial
                      color={new Color(project.color).offsetHSL(0.08, -0.35, 0.5)}
                      transparent
                      opacity={0.2 * globalReveal * smoothProgress}
                      side={DoubleSide}
                      depthWrite={false}
                    />
                  </mesh>
                </group>
              );
            })}

            <group ref={detailRingRef}>
              {detailSatellites.map((satellite, idx) => {
                // Same sequential logic for the satellite itself
                const start = idx * 0.12;
                const end = start + 0.45;
                const localProgress = THREE.MathUtils.clamp((revealProgress - start) / (end - start), 0, 1);
                const smoothProgress = THREE.MathUtils.smoothstep(localProgress, 0, 1);
                const itemScale = 0.5 + 0.5 * smoothProgress; // Pop in effect

                return (
                  <group key={satellite.label} rotation={[0, satellite.angle, 0]}>
                    <group position={[satellite.radius, satellite.height, 0]} scale={itemScale}>
                      <Satellite
                        scale={planetDisplayScale * 0.42}
                        baseColor={new Color(project.color).offsetHSL(0.02, -0.3, 0.25).getStyle()}
                        emissiveColor={new Color(project.color).offsetHSL(-0.06, 0.1, 0.2).getStyle()}
                        variant={idx % 4}
                        reveal={Math.min(1, Math.max(0.001, globalReveal * smoothProgress))}
                        seed={idx * 31}
                      />
                      <Float speed={2.2} rotationIntensity={0.25} floatIntensity={0.35}>
                        <Html
                          position={[0, planetDisplayScale * 0.68, 0]}
                          center
                          zIndexRange={[100, 0]} // Lower z-index range
                          style={{ pointerEvents: 'auto', opacity: smoothProgress, transition: 'opacity 0.2s' }}
                        >
                          <div
                            style={{
                              padding: '0.45rem 0.7rem',
                              borderRadius: '0.65rem',
                              background: 'rgba(15, 23, 42, 0.85)',
                              border: '1px solid rgba(148, 163, 184, 0.35)',
                              color: '#e2e8f0',
                              fontSize: '0.78rem',
                              whiteSpace: 'nowrap',
                              transform: `scale(${0.8 + 0.2 * smoothProgress})`,
                              transition: 'transform 0.2s'
                            }}
                          >
                            {satellite.label}
                          </div>
                        </Html>
                      </Float>
                    </group>
                  </group>
                );
              })}
            </group>
          </group>
        )}
      </group>
    </group>
  );
};

export default Planet;